import { ArrowLeftOutlined } from "@ant-design/icons"
import { Button, Card, Flex, Form, Skeleton, Typography } from "antd"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useParams } from "react-router"
import { Link, useNavigate } from "react-router-dom"

import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import type { RootState } from "../../../store.ts"
import { LeadLoadFailure } from "../components/LeadLoadFailure.tsx"
import { LEAD_STATUSES } from "../constants/lead-statuses.ts"
import { pickChangedFields } from "../LeadWorkspacePage/lead-workspace-patch.ts"
import { fetchLead, updateLead } from "../redux/lead.thunks.ts"
import { BudgetSummarySection } from "./components/BudgetSummarySection.tsx"
import { LeadNotEditable } from "./components/LeadNotEditable.tsx"

const { Title } = Typography

interface LeadFinancialValues {
  sales_price: number | null
  planned_payable: number | null
}

/**
 * The one surface left of the retired edit form. Sales Price and Planned
 * Payable have no workspace card that writes them — the Financial Snapshot
 * only reads — so they keep this page rather than losing their editor.
 */
const LeadFinancialPage = ({
  lead,
  saving,
  fetching,
  notFound,
  getLeadById,
  updateLeadById
}: ConnectedProps<typeof connector>) => {
  const { t } = useTranslation()
  const id = useParams()?.id as string
  const navigate = useNavigate()
  const [form] = Form.useForm<LeadFinancialValues>()

  useEffect(() => {
    if (id) getLeadById(id)
  }, [id, getLeadById])

  useEffect(() => {
    generateDocumentTitle("Lead Financials")
  }, [])

  // The form takes its values once, on mount, so it must not mount against a
  // lead left in the store by another page.
  const current = lead?.id === id ? lead : null

  if (fetching && !current)
    return <Skeleton.Node active className={"size-full"} />

  if (!current) {
    return (
      <LeadLoadFailure
        notFound={notFound}
        retrying={fetching}
        onRetry={() => getLeadById(id)}
      />
    )
  }

  if (current.status === LEAD_STATUSES.WON)
    return <LeadNotEditable lead={current} />

  const onSubmit = async (values: LeadFinancialValues) => {
    try {
      const pristine: LeadFinancialValues = {
        sales_price: current.sales_price,
        planned_payable: current.planned_payable
      }
      const changed = pickChangedFields(pristine, values)

      await updateLeadById({ id, ...changed }).unwrap()
      navigate(RoutesPaths.lead.view(id))
    } catch {
      // The thunk already surfaced the failure; the entries stay for a retry.
    }
  }

  return (
    <Card size={"small"}>
      <Form<LeadFinancialValues>
        form={form}
        layout={"vertical"}
        initialValues={{
          sales_price: current.sales_price,
          planned_payable: current.planned_payable
        }}
        onFinish={onSubmit}
      >
        <Flex vertical gap={"middle"}>
          <Flex gap={"small"} align={"center"}>
            <Link to={RoutesPaths.lead.view(id)}>
              <Button
                aria-label={t("leadFinancial.back")}
                icon={<ArrowLeftOutlined />}
              />
            </Link>
            <Title level={3} className={"m-0"}>
              {t("leadFinancial.title")}
            </Title>
          </Flex>

          <BudgetSummarySection />

          <Flex gap={"small"} justify={"end"}>
            <Link to={RoutesPaths.lead.view(id)}>
              <Button disabled={saving}>{t("leadFinancial.cancel")}</Button>
            </Link>
            <Button type={"primary"} htmlType={"submit"} loading={saving}>
              {t("leadFinancial.save")}
            </Button>
          </Flex>
        </Flex>
      </Form>
    </Card>
  )
}

const mapStateToProps = (state: RootState) => ({
  lead: state.lead.lead,
  saving: state.lead.saveFetching,
  fetching: state.lead.fetching,
  notFound: state.lead.notFound
})

const mapDispatchToProps = {
  getLeadById: fetchLead,
  updateLeadById: updateLead
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(LeadFinancialPage)
